import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { fetchLatestReleaseNote, markReleaseNoteRead } from 'src/api/releaseNotes';
import { LatestReleaseNote } from 'src/api/types/releaseNote';

interface ReleaseNotesContextValue {
  latestNote: LatestReleaseNote | null;
  isUnread: boolean;
  loading: boolean;
  markAsRead: () => Promise<void>;
}

const ReleaseNotesContext = createContext<ReleaseNotesContextValue | null>(null);

export const ReleaseNotesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [latestNote, setLatestNote] = useState<LatestReleaseNote | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    fetchLatestReleaseNote()
      .then((note) => {
        if (isMounted) setLatestNote(note);
      })
      .catch(() => {
        // No release notes to show is a fine fallback; don't surface an error toast for this.
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const markAsRead = useCallback(async () => {
    if (!latestNote || latestNote.isRead) return;

    setLatestNote((current) => (current ? { ...current, isRead: true } : current));

    try {
      await markReleaseNoteRead(latestNote.id);
    } catch {
      setLatestNote((current) => (current ? { ...current, isRead: false } : current));
    }
  }, [latestNote]);

  const value = useMemo(
    () => ({
      latestNote,
      isUnread: !!latestNote && !latestNote.isRead,
      loading,
      markAsRead
    }),
    [latestNote, loading, markAsRead]
  );

  return <ReleaseNotesContext.Provider value={value}>{children}</ReleaseNotesContext.Provider>;
};

export const useReleaseNotes = () => {
  const context = useContext(ReleaseNotesContext);
  if (!context) {
    throw new Error('useReleaseNotes must be used within ReleaseNotesProvider');
  }
  return context;
};
